<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { watch, ref } from "vue";

import { TDropButtonPosition } from "@/components/ui/buttons/drop-button.types";
import { useTypedIntl } from "@/locales/typed-intl";

import DropButton from "@/components/ui/buttons/drop-button.vue";

export interface IDropMenuItem {
  label: string;
  sublabel?: string;
  icon?: string;
  callback?: () => void;
  danger?: boolean;
  visible?: () => boolean;
  withDividerBefore?: boolean;
}

interface IProps {
  items: IDropMenuItem[];
  right?: boolean;
  position?: TDropButtonPosition;
}

interface IEmits {
  (e: "show"): void;
  (e: "hide"): void;
}

const emits = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  right: false,
  position: "bottom",
});

const { rawT } = useTypedIntl();

const showContent = ref<boolean>(false);
const button = ref(null);

function handleItemClick(item: IDropMenuItem) {
  if (item.callback) {
    item.callback();
  }

  showContent.value = false;
}

function getItemVisibility(item: IDropMenuItem): boolean {
  if (item.visible) {
    return item.visible();
  }

  return true;
}

watch(showContent, (newValue) => {
  if (newValue) {
    emits("show");
    return;
  }

  emits("hide");
});

onClickOutside(button, () => {
  showContent.value = false;
});
</script>

<template>
  <DropButton
    :right="props.right"
    :position="props.position"
  >
    <template #trigger>
      <slot />
    </template>

    <template #content>
      <ul class="flex flex-col w-full">
        <template
          v-for="(item, index) of $props.items"
          :key="index"
        >
          <li
            v-if="getItemVisibility(item)"
            class="w-full"
          >
            <div
              v-if="item.withDividerBefore"
              class="py-1 w-full"
            >
              <div class="border-t border-[#F2F2F2] w-full" />
            </div>

            <div
              :aria-label="item.label"
              class="!px-2 !py-2.5 menu-button w-full flex flex-col gap-2 rounded-xl"
              :data-test-id="`drop-menu-item-${item.label}`"
              role="button"
              tabindex="0"
              @click.stop="handleItemClick(item)"
              @keydown.enter.prevent="handleItemClick(item)"
            >
              <span
                class="font-semibold items-center flex w-full whitespace-nowrap text-sm leading-normal"
                :class="{ 'text-danger': item.danger, 'text-text-primary': !item.danger }"
              >
                <i
                  v-if="item.icon"
                  class="mr-2 text-base"
                  :class="item.icon"
                />
                {{ rawT(item.label) }}
              </span>
              <span
                v-if="item.sublabel"
                class="text-[#8F8F8F]"
              >
                {{ rawT(item.sublabel) }}
              </span>
            </div>
          </li>
        </template>
      </ul>
    </template>
  </DropButton>
</template>
